import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlerts } from 'hooks';
import { useAppContext } from 'hooks/useAppContext';
import { useCampaignById, useUpdateCampaign } from 'queries/UseCampaigns';
import { useDesignationGroups, useDesignations } from 'queries/UseDesignations';
import { SelectorOption } from '../../../components/AdvancedDesignationsSelector';
import { CampaignEditForm } from './CampaignEditForm';
import { INewCampaignSchema, NewCampaignSchema } from './NewCampaign.Schema';

export const CampaignEdit = (): JSX.Element => {
  const navigate = useNavigate();
  const [pushAlert] = useAlerts();

  const {
    selectedOrganization: { id: organizationId }
  } = useAppContext();

  const { campaignId } = useParams();
  const { data: campaign, isLoading: isCampaignLoading } = useCampaignById(
    organizationId,
    campaignId
  );

  // we also need to know which designations are currently set on this campaign
  const {
    data: campaignDesignations,
    isLoading: isCampaignDesignationsLoading
  } = useDesignations({
    organizationId,
    campaignId
  });

  // TODO : feed this data into the component so currently selected items are visible
  const {
    data: campaignDesignationGroups,
    isLoading: isCampaignDesignationGroupsLoading
  } = useDesignationGroups({
    organizationId,
    campaignId
  });

  const isLoading = isCampaignLoading || isCampaignDesignationsLoading;

  const { mutateAsync: updateCampaignMutation, isLoading: isCampaignUpdating } =
    useUpdateCampaign(organizationId, campaignId);

  const methods = useForm<INewCampaignSchema>({
    resolver: yupResolver(NewCampaignSchema),
    mode: 'onBlur'
  });

  const breadcrumbs = [
    {
      label: 'All Campaigns',
      path: '/g/fundraising/campaigns'
    },
    {
      label: 'Edit Campaign',
      path: ''
    }
  ];

  const handleCampaignUpdate = async (data: FieldValues) => {
    try {
      await updateCampaignMutation({
        title: data.title,
        startDate: data.startDate,
        endDate: data.endDate,
        goal: data.goalAmount ? Number(data.goalAmount) : undefined,
        designationIds: data.designations
          .filter((d: any) => d.type === 'individual')
          .map((d: any) => d.value as string),
        designationGroupIds: data.designations
          .filter((d: any) => d.type === 'group')
          .map((d: any) => d.value as string)
      });

      navigate(`/g/fundraising/campaigns/${campaignId}/`, {
        state: { showNewCampaignModal: false }
      });
    } catch (e) {
      pushAlert({
        title: 'Error updating campaign',
        description: `${e as string}`,
        severity: 'error',
        autoHideDuration: 10000
      });
    }
  };

  const localizeDate = (dateString: string): string => {
    if (!dateString) {
      return dateString;
    }
    const splitDates = dateString.split('-');
    const year = Number(splitDates[0]);
    const month = Number(splitDates[1]) - 1; // month is 0-indexed
    const day = Number(splitDates[2]);

    const recreatedDate = new Date(year, month, day);
    return recreatedDate.toUTCString();
  };

  useEffect(() => {
    if (campaign && campaignDesignations && campaignDesignationGroups) {
      // Initialize form with campaign data
      const { setValue, trigger } = methods;
      setValue('title', campaign.title);
      setValue('goalAmount', campaign.goal);
      setValue('startDate', localizeDate(campaign.startDate));
      setValue('endDate', localizeDate(campaign.endDate));

      const selectedDesignations: SelectorOption[] = [];

      campaignDesignations?.forEach((d) => {
        selectedDesignations.push({
          label: d.title,
          value: d.id,
          type: 'individual'
        });
      });

      campaignDesignationGroups?.forEach((dGroup) => {
        selectedDesignations.push({
          label: dGroup.name,
          value: dGroup.id,
          type: 'group',
          designationsInGroup: dGroup.designations
        });
      });

      setValue('designations', selectedDesignations);
      trigger('designations');
    }
  }, [campaign, campaignDesignations, campaignDesignationGroups, methods]);

  return (
    <CampaignEditForm
      formTitle="Edit Campaign"
      methods={methods}
      breadcrumbs={breadcrumbs}
      buttonLabel="Update Campaign"
      isLoading={isLoading || isCampaignUpdating}
      onSubmit={handleCampaignUpdate}
    />
  );
};
